// Pagina PUBLICA (sem auth) pra jogador pagar cobrança sem sair do dominio
// Futezap. Rota: /comunidade/pagar/:token. Fluxo:
//  1. GET dados da cobrança pelo token
//  2. Escolhe Pix ou Cartão
//  3. Pix: gera QR + copia-cola + polling 3s ate confirmar/expirar
//  4. Cartão: form tokenizado via MP SDK.js + submit sincrono
//  5. Confirmação = tela de sucesso
// Webhook /mp-payments (backend) confirma independente do fluxo aqui e cria
// entry no livro caixa do time — polling deste FE eh so pra UX.

import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Row, Col, Button, Space, Typography, Alert, Input, Form, message, Tabs, Spin } from 'antd';
import { CopyOutlined, CheckCircleFilled, WarningFilled } from '@ant-design/icons';
import api from '../../services/api';
import { load as loadMpSdk, isConfigured as mpConfigured } from '../../utils/mpSdk';

const { Text, Title, Paragraph } = Typography;
const PRIMARY = '#128C7E';
const POLL_MS = 3000;
const MAX_WAIT_MS = 15 * 60 * 1000; // 15 min pra Pix expirar (MP costuma dar ~30min)

const fmtBRL = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;

// Mapa amigável pt-BR pra status_detail comum do MP em decline de cartão.
const REJECTION_MESSAGES = {
  cc_rejected_insufficient_amount: 'Saldo insuficiente no cartão',
  cc_rejected_bad_filled_card_number: 'Número do cartão inválido',
  cc_rejected_bad_filled_date: 'Data de validade inválida',
  cc_rejected_bad_filled_security_code: 'CVV inválido',
  cc_rejected_bad_filled_other: 'Algum dado do cartão está errado',
  cc_rejected_high_risk: 'Pagamento recusado por segurança',
  cc_rejected_max_attempts: 'Muitas tentativas. Tenta outro cartão',
  cc_rejected_call_for_authorize: 'Autorização necessária no seu banco',
  cc_rejected_duplicated_payment: 'Pagamento duplicado detectado',
  cc_rejected_card_disabled: 'Cartão desabilitado',
  cc_rejected_card_error: 'Erro no cartão',
  cc_rejected_blacklist: 'Cartão bloqueado',
  cc_rejected_other_reason: 'Cartão recusado',
};

export default function PayCharge() {
  const { token } = useParams();
  const [charge, setCharge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [method, setMethod] = useState('pix');
  const [pix, setPix] = useState(null);
  const [pixLoading, setPixLoading] = useState(false);
  const [pixStatus, setPixStatus] = useState(null); // 'paid' | 'failed' | 'expired' | null
  const [pixSeconds, setPixSeconds] = useState(0);
  const pollingRef = useRef(false);
  const [form] = Form.useForm();
  const [cardSubmitting, setCardSubmitting] = useState(false);
  const cardSubmittingRef = useRef(false);
  const [cardError, setCardError] = useState(null);
  const [cardSuccess, setCardSuccess] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setLoadError(null);
    try {
      const r = await api.get(`/public/community/charges/pay/${token}`);
      setCharge(r.data);
    } catch (e) {
      setLoadError(e.response?.data?.message || 'Link inválido');
    } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  // Se já pago, mostra sucesso direto
  useEffect(() => {
    if (charge?.status === 'paid') setPixStatus('paid');
  }, [charge]);

  const generatePix = async () => {
    setPixLoading(true);
    try {
      const r = await api.post(`/public/community/charges/pay/${token}/pix`);
      setPix(r.data);
      setPixSeconds(MAX_WAIT_MS / 1000);
      startPolling();
    } catch (e) {
      const status = e.response?.status;
      if (status === 503) message.error('Pagamento por Pix indisponível. Fala com o organizador.');
      else message.error(e.response?.data?.message || 'Falhou gerar Pix. Tenta de novo.');
    } finally { setPixLoading(false); }
  };

  const startPolling = () => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    const started = Date.now();
    const tick = async () => {
      if (!pollingRef.current) return;
      const elapsed = Date.now() - started;
      setPixSeconds(Math.max(0, Math.round((MAX_WAIT_MS - elapsed) / 1000)));
      if (elapsed > MAX_WAIT_MS) {
        pollingRef.current = false; setPixStatus('expired'); return;
      }
      try {
        const r = await api.get(`/public/community/charges/pay/${token}/status`);
        if (r.data.status === 'paid') {
          pollingRef.current = false; setPixStatus('paid');
          return;
        }
        if (r.data.status === 'failed') {
          pollingRef.current = false; setPixStatus('failed');
          return;
        }
      } catch (e) {
        if (e.response?.status === 404) { pollingRef.current = false; setPixStatus('failed'); return; }
      }
      setTimeout(tick, POLL_MS);
    };
    setTimeout(tick, POLL_MS);
  };
  useEffect(() => () => { pollingRef.current = false; }, []);

  const copyPix = () => {
    if (!pix?.qr_code) return;
    try {
      navigator.clipboard.writeText(pix.qr_code);
      message.success('Código Pix copiado');
    } catch {
      // Fallback pra browsers velhos
      const ta = document.createElement('textarea');
      ta.value = pix.qr_code; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); message.success('Código copiado'); }
      catch { message.info('Selecione o código e copie manualmente'); }
      document.body.removeChild(ta);
    }
  };

  const submitCard = async (values) => {
    if (cardSubmittingRef.current) return;
    cardSubmittingRef.current = true;
    setCardSubmitting(true); setCardError(null);
    try {
      const mp = await loadMpSdk();
      const expiry = String(values.expiry || '').replace(/\s/g, '');
      const [mm, yy] = expiry.split('/');
      const cardToken = await mp.createCardToken({
        cardNumber: String(values.card_number || '').replace(/\s/g, ''),
        cardholderName: values.holder_name,
        cardExpirationMonth: mm,
        cardExpirationYear: yy?.length === 2 ? '20' + yy : yy,
        securityCode: values.cvv,
        identificationType: 'CPF',
        identificationNumber: String(values.cpf || '').replace(/\D/g, ''),
      });
      if (!cardToken?.id) {
        setCardError('Não deu pra validar o cartão. Confere os dados.');
        return;
      }
      const r = await api.post(`/public/community/charges/pay/${token}/card`, {
        card_token: cardToken.id,
        payer_cpf: String(values.cpf).replace(/\D/g, ''),
      });
      if (r.data.status === 'paid') {
        setCardSuccess(true);
      } else if (r.data.status === 'pending') {
        setCardSuccess(true);
        message.info('Pagamento em análise, avisamos o organizador assim que aprovar');
      } else {
        setCardError('Cartão não pôde ser processado. Tenta outro.');
      }
    } catch (e) {
      const backendMsg = e.response?.data?.message;
      const detail = e.response?.data?.status_detail;
      if (detail && REJECTION_MESSAGES[detail]) setCardError(REJECTION_MESSAGES[detail]);
      else if (e.response?.status === 402) setCardError(backendMsg || 'Cartão recusado');
      else if (e.response?.status === 503) setCardError('Cartão indisponível agora. Usa Pix.');
      else setCardError(backendMsg || 'Erro ao processar pagamento');
    } finally {
      cardSubmittingRef.current = false;
      setCardSubmitting(false);
    }
  };

  // Loading state
  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><Spin size="large" /></div>;
  if (loadError) return (
    <div style={{ padding: 24, maxWidth: 500, margin: '40px auto' }}>
      <Alert type="error" showIcon message="Link inválido" description={loadError} />
    </div>
  );
  if (!charge) return null;

  // Success state (Pix ou Card)
  if (pixStatus === 'paid' || cardSuccess) {
    return (
      <div style={{ padding: 24, maxWidth: 500, margin: '40px auto', textAlign: 'center' }}>
        <Card>
          <CheckCircleFilled style={{ fontSize: 72, color: PRIMARY, marginBottom: 16 }} />
          <Title level={3} style={{ color: PRIMARY, marginTop: 0 }}>Pagamento confirmado!</Title>
          <Paragraph>
            Recebemos {fmtBRL(charge.amount_brl)} do <strong>{charge.batch_name}</strong>.
          </Paragraph>
          <Paragraph type="secondary">
            Já avisamos o {charge.team_name} que você pagou.
          </Paragraph>
        </Card>
      </div>
    );
  }

  // Já pago (verificado no load, redundante mas defensivo)
  if (charge.status === 'paid') {
    return (
      <div style={{ padding: 24, maxWidth: 500, margin: '40px auto', textAlign: 'center' }}>
        <Card>
          <CheckCircleFilled style={{ fontSize: 72, color: PRIMARY, marginBottom: 16 }} />
          <Title level={3} style={{ color: PRIMARY, marginTop: 0 }}>Já pago</Title>
          <Paragraph>Essa cobrança já foi paga. Nada a fazer.</Paragraph>
        </Card>
      </div>
    );
  }

  // Terminal states (refunded, cancelled)
  if (charge.status !== 'pending' && charge.status !== 'error') {
    return (
      <div style={{ padding: 24, maxWidth: 500, margin: '40px auto', textAlign: 'center' }}>
        <Card>
          <WarningFilled style={{ fontSize: 72, color: '#faad14', marginBottom: 16 }} />
          <Title level={3} style={{ marginTop: 0 }}>Cobrança {charge.status}</Title>
          <Paragraph>Essa cobrança não está mais aberta pra pagamento.</Paragraph>
        </Card>
      </div>
    );
  }

  const showCard = charge.methods?.credit_card && mpConfigured();
  const showPix = charge.methods?.pix !== false;

  return (
    <div style={{ padding: 16, maxWidth: 560, margin: '0 auto' }}>
      <Card
        style={{ marginTop: 16 }}
        styles={{ body: { padding: 20 } }}
      >
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Cobrança de</Text>
          <Title level={4} style={{ margin: '4px 0', color: PRIMARY }}>{charge.team_name}</Title>
          <Text type="secondary">{charge.batch_name}</Text>
        </div>

        <Row justify="space-between" align="middle" style={{ background: '#f5f5f5', padding: 16, borderRadius: 8, marginBottom: 20 }}>
          <Col>
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Pra pagar</Text>
            <Text strong>{charge.recipient_name}</Text>
            {charge.description && (
              <div style={{ marginTop: 4 }}><Text type="secondary" style={{ fontSize: 12 }}>{charge.description}</Text></div>
            )}
          </Col>
          <Col>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', textAlign: 'right' }}>Valor</Text>
            <Text strong style={{ fontSize: 24, color: PRIMARY }}>{fmtBRL(charge.amount_brl)}</Text>
          </Col>
        </Row>

        <Tabs
          activeKey={method}
          onChange={setMethod}
          items={[
            showPix && { key: 'pix', label: 'Pix' },
            showCard && { key: 'card', label: 'Cartão' },
          ].filter(Boolean)}
        />

        {method === 'pix' && (
          <div style={{ marginTop: 16 }}>
            {!pix && !pixStatus && (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Alert type="info" showIcon
                  message="Pix na hora"
                  description="Clica no botão pra gerar o QR Code e o código copia-e-cola. A confirmação é automática." />
                <Button type="primary" block size="large" loading={pixLoading}
                  style={{ background: PRIMARY, borderColor: PRIMARY }}
                  onClick={generatePix}>
                  Gerar Pix agora
                </Button>
              </Space>
            )}

            {pix && !pixStatus && (
              <Space direction="vertical" size="middle" style={{ width: '100%', alignItems: 'center' }}>
                <img src={`data:image/png;base64,${pix.qr_code_base64}`} alt="QR Code Pix"
                  style={{ width: 240, height: 240, border: '1px solid #eee', borderRadius: 8 }} />
                <Text type="secondary" style={{ fontSize: 13, textAlign: 'center' }}>
                  Aponta o app do seu banco pro QR ou copia o código:
                </Text>
                <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 8, wordBreak: 'break-all',
                  fontFamily: 'monospace', fontSize: 11, maxWidth: '100%', textAlign: 'center' }}>
                  {pix.qr_code}
                </div>
                <Button icon={<CopyOutlined />} onClick={copyPix} type="primary" block
                  style={{ background: PRIMARY, borderColor: PRIMARY }}>
                  Copiar código Pix
                </Button>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Aguardando pagamento… atualiza sozinho quando confirmar
                  {pixSeconds > 0 && ` (${Math.floor(pixSeconds / 60)}min ${pixSeconds % 60}s pra expirar)`}
                </Text>
              </Space>
            )}

            {pixStatus === 'expired' && (
              <Alert type="warning" showIcon
                message="Pix expirou"
                description="Gera um novo Pix pra pagar."
                action={<Button size="small" onClick={() => { setPix(null); setPixStatus(null); }}>Gerar novo</Button>} />
            )}
            {pixStatus === 'failed' && (
              <Alert type="error" showIcon
                message="Pagamento não confirmou"
                description="Tenta gerar de novo ou paga com cartão."
                action={<Button size="small" onClick={() => { setPix(null); setPixStatus(null); }}>Tentar de novo</Button>} />
            )}
          </div>
        )}

        {method === 'card' && !showCard && (
          <Alert type="warning" showIcon message="Cartão indisponível pra essa cobrança" description="Usa Pix pra pagar." style={{ marginTop: 16 }} />
        )}

        {method === 'card' && showCard && (
          <Form form={form} layout="vertical" onFinish={submitCard} disabled={cardSubmitting} style={{ marginTop: 16 }}>
            <Form.Item label="Nome no cartão" name="holder_name" rules={[{ required: true, message: 'Obrigatório' }]}>
              <Input placeholder="Como está no cartão" autoComplete="cc-name" />
            </Form.Item>
            <Form.Item label="Número do cartão" name="card_number" rules={[
              { required: true, message: 'Obrigatório' },
              { pattern: /^[\d\s]{13,19}$/, message: 'Número inválido' },
            ]}>
              <Input placeholder="0000 0000 0000 0000" maxLength={19} inputMode="numeric" autoComplete="cc-number" />
            </Form.Item>
            <Row gutter={12}>
              <Col xs={12}>
                <Form.Item label="Validade" name="expiry" rules={[
                  { required: true, message: 'Obrigatório' },
                  { pattern: /^\d{2}\s?\/\s?\d{2}$/, message: 'MM/AA' },
                ]}>
                  <Input placeholder="MM/AA" maxLength={5} inputMode="numeric" autoComplete="cc-exp" />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item label="CVV" name="cvv" rules={[
                  { required: true, message: 'Obrigatório' },
                  { pattern: /^\d{3,4}$/, message: '3 ou 4 dígitos' },
                ]}>
                  <Input placeholder="000" maxLength={4} inputMode="numeric" autoComplete="cc-csc" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="CPF do titular" name="cpf" rules={[
              { required: true, message: 'Obrigatório' },
              { pattern: /^\d{11}$/, message: 'Só dígitos (11)' },
            ]}>
              <Input placeholder="00000000000" maxLength={11} inputMode="numeric" />
            </Form.Item>
            {cardError && (
              <Alert type="error" showIcon message={cardError} style={{ marginBottom: 12 }} />
            )}
            <Button type="primary" htmlType="submit" block size="large" loading={cardSubmitting}
              style={{ background: PRIMARY, borderColor: PRIMARY }}>
              Pagar {fmtBRL(charge.amount_brl)}
            </Button>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', textAlign: 'center', marginTop: 8 }}>
              Seus dados de cartão nunca chegam nos nossos servidores. São enviados direto pro Mercado Pago (PCI-safe).
            </Text>
          </Form>
        )}
      </Card>

      <div style={{ textAlign: 'center', marginTop: 16 }}>
        <Text type="secondary" style={{ fontSize: 11 }}>
          Pagamento seguro processado pelo Mercado Pago · Futezap
        </Text>
      </div>
    </div>
  );
}
